import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import icon from '../../../brand/generated/sidebar/cars.svg';
import HookForm from '../../../components/HookForm/HookForm';
import getComplectations from '../../../api/cars/getComplectations';
import SelectEngine from '../../../components/Selects/SelectEngine';
import SelectCarcase from '../../../components/Selects/SelectCarcase';
import editComplectation from '../../../api/cars/editComplectation';
import SelectWd from '../../../components/Selects/SelectWd';
import { textStyles } from '../../../constants/textStyles';
import LoaderInline from '../../../components/Loader/LoaderInline';
import alert from '../../../helpers/alert';
import styles from '../../../components/Inputs/Checkbox/checkbox.module.css';
import { EQUIPMENT_VALUE_PLACEHOLDER, SAFETY_VALUE_PLACEHOLDER } from './optionPlaceholders';

const CheckboxField = ({ value, label, onChange }) => (
  <div className={styles.checkbox}>
    <input
      type="checkbox"
      id="restyling"
      checked={!!value}
      onChange={(e) => onChange(e.target.checked)}
    />
    <label htmlFor="restyling">{label}</label>
  </div>
);

const formStyles = {
  complectation: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: '1fr 1fr 1fr',
  },
  equipments: {},
};

const additionalEquipments = Array.from({ length: 5 }, (_, index) => ({
  title: '',
  name: `equip-${index + 1}`,
}));

// Опция — пара «название — значение» («Датчики парковки» — «Задние»): так её
// показывает карточка Halyk Market. Название — в поле `<prefix>_<ключ>`, значение —
// в `<prefix>value_<ключ>` с тем же ключом (id строки или equip-N у новой).
const optionInputs = (rows, prefix, label, placeholder, valuePlaceholder, onRemove, removedRef) =>
  [...rows, ...additionalEquipments].flatMap((option, index) => {
    const key = option.id || option.name;
    const name = `${prefix}_${key}`;
    const hide = () => removedRef.current.has(name);
    return [
      {
        name,
        label: `${label} ${index + 1}`,
        placeholder,
        defaultValue: option.title,
        style: { gridColumn: '1 / span 1' },
        removable: false,
        onRemove,
        hide,
      },
      {
        name: `${prefix}value_${key}`,
        label: 'Значение',
        placeholder: valuePlaceholder,
        defaultValue: option.subtitle || '',
        style: { gridColumn: '2 / span 2' },
        removable: false,
        hide,
      },
    ];
  });

// Обратно — строки для бэкенда: название обязательно, пустое значение не отправляем.
const optionRows = (values, prefix, removed) =>
  Object.keys(values)
    .filter((key) => key.startsWith(`${prefix}_`) && !removed.has(key) && String(values[key] || '').trim())
    .map((key) => {
      const suffix = key.slice(prefix.length + 1);
      return {
        id: +suffix || undefined,
        title: values[key],
        subtitle: String(values[`${prefix}value_${suffix}`] || '').trim() || undefined,
      };
    });

const CarComplectationsEdit = () => {
  const { containerId, complectationId } = useParams();

  const [complectation, setComplectation] = useState([]);
  const [safely, setSafely] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [removedFields, setRemovedFields] = useState(() => new Set());
  const removedFieldsRef = useRef(new Set());
  // Номер загрузки данных — ключ формы. Новые строки вводятся в поля без id
  // (equipment_equip-N / comfort_equip-N). После сохранения они приходят с
  // сервера уже с id и встают в СВОИ поля, но react-hook-form продолжал
  // помнить текст в старых полях без id — и следующее «Сохранить» отправляло
  // те же строки как новые. Бэкенд честно добавлял их ещё раз: у Suburban пять
  // строк безопасности превратились в десять. Со сменой ключа форма
  // поднимается заново и знает только то, что вернул сервер.
  const [formVersion, setFormVersion] = useState(0);

  const onRemoveField = (name) => {
    setRemovedFields((prev) => {
      const next = new Set(prev);
      next.add(name);
      removedFieldsRef.current = next;
      return next;
    });
  };

  const init = useCallback(() => {
    getComplectations({ containerId })?.then((res) => {
      const item = res?.data?.find((i) => +i?.id === +complectationId);
      const nextRemoved = new Set();
      removedFieldsRef.current = nextRemoved;
      setRemovedFields(nextRemoved);

      setComplectation([
        {
          name: 'title',
          label: 'Комплектация',
          placeholder: 'Выберите из списка',
          defaultValue: item?.title,
        },
        {
          name: 'engineId',
          label: 'Тип топлива',
          component: SelectEngine,
          placeholder: 'Выберите из списка',
          defaultValue: { value: item?.engine.id, label: item?.engine.title },
        },
        {
          name: 'carcaseId',
          label: 'Тип топлива',
          component: SelectCarcase,
          placeholder: 'Выберите из списка',
          defaultValue: { value: item?.carcase.id, label: item?.carcase.title },
        },
        {
          name: 'wdId',
          label: 'Тип привода',
          component: SelectWd,
          placeholder: 'Выберите из списка',
          defaultValue: { value: item?.wd.id, label: item?.wd.title },
        },
        {
          name: 'engine_volume',
          label: 'Объем двигателя',
          placeholder: 'Выберите из списка',
          defaultValue: item?.engine_volume,
        },
        {
          name: 'power_value',
          label: 'Л.C.',
          defaultValue: item?.power_value,
        },
        {
          name: 'transmission',
          label: 'Коробка передач',
          defaultValue: item?.transmission,
        },
        {
          name: 'seats',
          label: 'Количество мест',
          defaultValue: item?.seats,
        },
        {
          name: 'generation',
          label: 'Поколение модели автомобиля',
          placeholder: 'Укажите поколение',
          defaultValue: item?.generation,
        },
        {
          name: 'restyling',
          label: 'Признак рестайлинга модели автомобиля',
          component: CheckboxField,
          defaultValue: item?.restyling || false,
        },
        {
          name: '',
          component: () => <p style={textStyles.formTitle}>Комфорт и Оборудование</p>,
          style: { gridColumn: '1 / span 3' },
        },
      ]);

      setEquipments(
        optionInputs(
          item?.complectation_equipment || [],
          'equipment',
          'Оборудование',
          'Введите название оборудования',
          EQUIPMENT_VALUE_PLACEHOLDER,
          onRemoveField,
          removedFieldsRef
        )
      );
      setSafely(
        optionInputs(
          item?.complectation_safely || [],
          'comfort',
          'Безопасность',
          'Введите название',
          SAFETY_VALUE_PLACEHOLDER,
          onRemoveField,
          removedFieldsRef
        )
      );
      setFormVersion((version) => version + 1);
    });
  }, [containerId, complectationId]);

  useEffect(() => {
    setComplectation([]);
    setEquipments([]);
    setSafely([]);
    init();
  }, [init]);


  const save = async (values) => {
    try {
      await editComplectation(complectationId, {
        title: values?.title,
        wdId: values?.wdId?.value,
        engineId: values?.engineId?.value,
        carcaseId: values?.carcaseId?.value,
        power_value: values?.power_value,
        engine_volume: values?.engine_volume,
        transmission: values?.transmission,
        seats: values?.seats,
        generation: values?.generation,
        restyling: values?.restyling || false,
        complectation_equipment: optionRows(values, 'equipment', removedFields),
        complectation_safely: optionRows(values, 'comfort', removedFields),
      });
      alert.success("Данные успешно сохранены");
      init();
    } catch(e) {
      alert.error(e);
    }
  };

  if (complectation?.length === 0) {
    return <LoaderInline />;
  }

  return (
    <>
      <Header title={'Комплектация ' + complectationId} icon={icon} />
      <div style={{ paddingBottom: '20px', marginTop: 20 }}>
        <HookForm
          key={formVersion}
          title={'Комплектация'}
          grid={formStyles.complectation}
          border
          inputs={[
            ...complectation,
            ...equipments,
            {
              name: '',
              component: () => <p style={textStyles.formTitle}>Безопасность</p>,
              style: { gridColumn: '1 / span 3' },
            },
            ...safely,
          ]}
          style={{ margin: 0 }}
          onSubmit={save}
          action={'Сохранить'}
        />
      </div>
    </>
  );
};

export default CarComplectationsEdit;
